/**
 * WorkspaceConstitutionPane — Read-only governance constitution inspector
 *
 * Surfaces charters, boundaries, doctrines, and constraint refs.
 * Consolidates 'constitutional-inspectability', 'adaptive-constitution',
 * 'architecture-governance-constitutional', and 'architecture-governance-dashboard'
 * panel concepts from UnifiedWorkspace into a single OperationalPaneModel pane.
 *
 * Shell B migration v1: replaces ConstitutionalInspectabilityPanel.tsx and
 * AdaptiveConstitutionPanel.tsx (Tailwind, mock data) with OperationalPaneModel shape.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Visibility of constitution/charters/doctrine does NOT imply approval,
 * authorization, or instruction to act. No legal conclusions.
 */

import React, { useState } from 'react';
import type {
  ConstitutionPaneModel,
  CharterEntry,
  BoundaryEntry,
  DoctrineEntry,
} from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceConstitutionPaneProps {
  model: ConstitutionPaneModel;
}

type TabId = 'charters' | 'boundaries' | 'doctrines';

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 600,
      color: NEUTRAL.text500,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      marginBottom: SPACING.sp2,
    }}>
      {title}
    </div>
  );
}

function CharterRow({ charter }: { charter: CharterEntry }) {
  return (
    <div style={{
      padding: `${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      borderLeft: `2px solid ${charter.active ? SEMANTIC.stabilized.border : NEUTRAL.borderSubtle}`,
      marginBottom: SPACING.sp1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACING.sp2 }}>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: NEUTRAL.text500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {charter.charterType}
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, flexShrink: 0 }}>{charter.domain}</span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: charter.active ? SEMANTIC.stabilized.text : NEUTRAL.text300,
          backgroundColor: charter.active ? SEMANTIC.stabilized.chip : NEUTRAL.bg500,
          padding: '1px 5px',
          borderRadius: 2,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.3,
          flexShrink: 0,
        }}>
          {charter.active ? 'active' : 'inactive'}
        </span>
      </div>
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontFamily: TYPOGRAPHY.mono.family,
        color: NEUTRAL.text300,
        marginTop: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {charter.charterRef}
      </div>
    </div>
  );
}

function BoundaryRow({ boundary }: { boundary: BoundaryEntry }) {
  return (
    <div style={{
      padding: `${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      borderLeft: `2px solid ${boundary.enforced ? SEMANTIC.stabilized.border : SEMANTIC.refusal.border}`,
      marginBottom: SPACING.sp1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: SPACING.sp2 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: NEUTRAL.text500 }}>
            {boundary.boundaryType}
          </div>
          <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginTop: 2 }}>
            {boundary.description}
          </div>
        </div>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: boundary.enforced ? SEMANTIC.stabilized.text : SEMANTIC.refusal.text,
          backgroundColor: boundary.enforced ? SEMANTIC.stabilized.chip : SEMANTIC.refusal.chip,
          padding: '1px 5px',
          borderRadius: 2,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.3,
          flexShrink: 0,
        }}>
          {boundary.enforced ? 'enforced' : 'not enforced'}
        </span>
      </div>
    </div>
  );
}

function DoctrineRow({ doctrine }: { doctrine: DoctrineEntry }) {
  return (
    <div style={{
      padding: `${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      borderLeft: `2px solid ${doctrine.active ? SEMANTIC.stabilized.border : NEUTRAL.borderSubtle}`,
      marginBottom: SPACING.sp1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACING.sp2 }}>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: NEUTRAL.text500, flex: 1 }}>
          {doctrine.label}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: doctrine.active ? SEMANTIC.stabilized.text : NEUTRAL.text300,
          backgroundColor: doctrine.active ? SEMANTIC.stabilized.chip : NEUTRAL.bg500,
          padding: '1px 5px',
          borderRadius: 2,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.3,
          flexShrink: 0,
        }}>
          {doctrine.active ? 'active' : 'inactive'}
        </span>
      </div>
      <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginTop: 2 }}>
        {doctrine.invariant}
      </div>
    </div>
  );
}

export const WorkspaceConstitutionPane: React.FC<WorkspaceConstitutionPaneProps> = ({ model }) => {
  const [activeTab, setActiveTab] = useState<TabId>('boundaries');

  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Constitution pane error: {model.error}
      </div>
    );
  }

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: 'boundaries', label: 'Boundaries', count: model.totalBoundaries },
    { id: 'charters', label: 'Charters', count: model.totalCharters },
    { id: 'doctrines', label: 'Doctrines', count: model.totalDoctrines },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Constitution
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.enforcedBoundaryCount}/{model.totalBoundaries} boundaries enforced
        </span>
      </div>

      {/* Safety strip */}
      <div style={{
        display: 'flex',
        gap: SPACING.sp2,
        flexWrap: 'wrap',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        flexShrink: 0,
      }}>
        {['READ-ONLY', 'PROOF-ONLY', 'VISIBILITY ≠ APPROVAL'].map(label => (
          <span key={label} style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase' as const,
            letterSpacing: 0.3,
          }}>
            {label}
          </span>
        ))}
      </div>

      {/* Unenforced boundary warning */}
      {model.totalBoundaries > 0 && model.enforcedBoundaryCount < model.totalBoundaries && (
        <div style={{
          padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
          backgroundColor: SEMANTIC.refusal.chip,
          border: `0 0 1px 0 solid ${SEMANTIC.refusal.border}`,
          fontSize: TYPOGRAPHY.micro.size,
          color: SEMANTIC.refusal.text,
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {model.totalBoundaries - model.enforcedBoundaryCount} boundary/boundaries not enforced. Human review required.
        </div>
      )}

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        borderBottom: `1px solid ${NEUTRAL.border}`,
        flexShrink: 0,
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? NEUTRAL.text600 : NEUTRAL.text400,
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${SEMANTIC.neutral.border}` : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>
        {activeTab === 'boundaries' && (
          <>
            <SectionHeader title={`Boundaries (${model.boundaries.length})`} />
            {model.boundaries.length === 0 ? (
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>No boundaries</div>
            ) : (
              model.boundaries.map(b => <BoundaryRow key={b.boundaryId} boundary={b} />)
            )}
          </>
        )}
        {activeTab === 'charters' && (
          <>
            <SectionHeader title={`Charters (${model.charters.length})`} />
            {model.charters.length === 0 ? (
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>No charters</div>
            ) : (
              model.charters.map(c => <CharterRow key={c.charterId} charter={c} />)
            )}
          </>
        )}
        {activeTab === 'doctrines' && (
          <>
            <SectionHeader title={`Doctrines (${model.doctrines.length})`} />
            {model.doctrines.length === 0 ? (
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>No doctrines</div>
            ) : (
              model.doctrines.map(d => <DoctrineRow key={d.doctrineId} doctrine={d} />)
            )}
          </>
        )}

        {/* Constraint refs */}
        {model.constraintRefs.length > 0 && (
          <div style={{ marginTop: SPACING.sp3 }}>
            <SectionHeader title={`Constraint Refs (${model.constraintRefs.length})`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.constraintRefs.map((r, i) => (
                <div key={i} style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  fontFamily: TYPOGRAPHY.mono.family,
                  color: NEUTRAL.text400,
                  backgroundColor: NEUTRAL.bg700,
                  padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
                  borderRadius: 2,
                }}>
                  {r}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
